import * as cookie from 'cookie';
import { redirect } from 'react-router';
import { HORIZON_ENABLED } from '#/rendering/state/renderingState';
import { analyticsQueryParamKeys } from '#/utils/analytics/setup/configureAnalyticsUserData';
import { isomorphicIsExternalUrl } from '#/utils/url/isomorphicIsExternalUrl';
import {
  type FlashCookie,
  type Flash,
  FLASHES_COOKIE,
} from '#/zustand-stores/flashesStore';

type Args = {
  request: Request;
  to: string;
  flash?: string | Flash;
  status?: number;
};

/**
 * This utility should always be used in place of react-router's native
 * redirect because it allows us to control anything that needs to happen
 * such as forwarding analytics query params.
 *
 * It also allows us to easily add extra functionality such as passing a flash
 * message via cookie
 */
export function idealistRedirect({ request, to, flash, status }: Args) {
  const currentUrl = new URL(request.url);

  if (isomorphicIsExternalUrl({ url: to, appUrl: currentUrl.origin }))
    return redirect(to);

  const headers: Record<string, string> = {};

  if (HORIZON_ENABLED && flash) {
    const twoDaysFromNow = new Date();
    twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 2);

    const flashCookieItem = {
      type: 'flash',
      data:
        typeof flash === 'string'
          ? { message: flash, style: 'success' }
          : flash,
    } satisfies FlashCookie;

    headers['Set-Cookie'] = cookie.serialize(
      FLASHES_COOKIE,
      Buffer.from(JSON.stringify([flashCookieItem]), 'utf-8').toString(
        'base64',
      ),
      { path: '/', expires: twoDaysFromNow },
    );
  }

  const newUrl = new URL(
    to,
    HORIZON_ENABLED ? process.env.IDEALIST_APP_URL : currentUrl.origin,
  );

  for (const param of analyticsQueryParamKeys) {
    if (currentUrl.searchParams.has(param))
      newUrl.searchParams.set(param, currentUrl.searchParams.get(param) ?? '');
  }

  return redirect(newUrl.toString(), { headers, status });
}
